'use strict';

const libPictView = require('pict-view');
const libFrameworkClient = require('../providers/FrameworkClient.js');
const libMarkdownRender = require('./MarkdownRender.js');

/**
 * HelpDrawer -- the contextual product-help panel docked on the right of the shell.
 *
 * Opened by F1 or the top-bar "?" (the app drives the shell panel + calls refreshForRoute). On open it
 * reads the current route, matches it to a help topic from /1.0/help/_topics.json, and renders that
 * topic's help (its landing doc, with the topic's other docs as tabs). A screen with no topic falls
 * back to the help home (index.md).
 *
 * It is NOT the inline-documentation module (that single instance backs the full-page #/docs viewer).
 * It reuses the same read-only renderer the foundational docs use -- MarkdownRender over the
 * pict-section-content provider -- so help reads identically, in a UX built for a narrow drawer.
 */
class PictViewHelpDrawer extends libPictView
{
	constructor(pFable, pOptions, pServiceHash)
	{
		super(pFable, pOptions, pServiceHash);
		this._HelpBaseURL = '/1.0/help/';
		this._Topics = null;        // cached _topics.json, fetched once
		this._TopicsLoading = null; // in-flight promise so concurrent opens share one fetch
		this._ActivePath = '';
	}

	onAfterRender(pRenderable, pAddress, pRecord, pContent)
	{
		if (this.pict.CSSMap) { this.pict.CSSMap.injectCSS(); }
		// The shell re-renders this bound ContentView on every panel expand, so refreshing here is what
		// makes opening the drawer show help for the screen the user is on right now (and it preloads at
		// creation). Navigation while the drawer is already open is handled by the app's hashchange hook.
		this.refreshForRoute();
		return super.onAfterRender(pRenderable, pAddress, pRecord, pContent);
	}

	_contentProvider() { return this.pict.providers.Content; }
	_el(pID) { return (typeof document !== 'undefined') ? document.getElementById(pID) : null; }

	// The current app route, e.g. '#/checklists' -> '/checklists'.
	_currentRoute()
	{
		let tmpHash = (typeof window !== 'undefined' && window.location && window.location.hash) ? window.location.hash : '';
		let tmpRoute = String(tmpHash).replace(/^#/, '');
		if (!tmpRoute) { tmpRoute = '/'; }
		return tmpRoute;
	}

	// Load the topics manifest once (cached). Always resolves to an object (empty on any failure).
	_loadTopics()
	{
		if (this._Topics) { return Promise.resolve(this._Topics); }
		if (this._TopicsLoading) { return this._TopicsLoading; }
		this._TopicsLoading = libFrameworkClient.getJSON(this.pict, this._HelpBaseURL + '_topics.json')
			.catch(() => ({}))
			.then((pTopics) => { this._Topics = pTopics || {}; this._TopicsLoading = null; return this._Topics; });
		return this._TopicsLoading;
	}

	// Match a route to the most specific topic whose Routes cover it (exact, or a '/prefix/*' wildcard).
	_resolveTopic(pTopics, pRoute)
	{
		let tmpBest = null;
		let tmpBestLen = -1;
		Object.keys(pTopics || {}).forEach((pCode) =>
		{
			let tmpTopic = pTopics[pCode];
			let tmpRoutes = (tmpTopic && Array.isArray(tmpTopic.Routes)) ? tmpTopic.Routes : [];
			for (let i = 0; i < tmpRoutes.length; i++)
			{
				let tmpPattern = String(tmpRoutes[i] || '');
				let tmpMatch = false;
				if (tmpPattern.slice(-2) === '/*')
				{
					let tmpPrefix = tmpPattern.slice(0, -2);
					tmpMatch = (pRoute === tmpPrefix) || (pRoute.indexOf(tmpPrefix + '/') === 0);
				}
				else { tmpMatch = (pRoute === tmpPattern); }
				if (tmpMatch && tmpPattern.length > tmpBestLen) { tmpBest = tmpTopic; tmpBestLen = tmpPattern.length; }
			}
		});
		return tmpBest;
	}

	// The entry point the app calls on each open + on navigation while open.
	refreshForRoute()
	{
		if (!this._el('HelpDrawer-Body')) { return; } // not rendered into the panel yet
		let tmpRoute = this._currentRoute();
		this._loadTopics().then((pTopics) =>
		{
			// The route may have changed during the fetch; re-read so we show what is on screen now.
			let tmpTopic = this._resolveTopic(pTopics, this._currentRoute());
			if (tmpTopic) { this._renderTopic(tmpTopic); }
			else { this._renderNoTopic(); }
		});
	}

	_renderTopic(pTopic)
	{
		let tmpTitle = this._el('HelpDrawer-Title');
		if (tmpTitle) { tmpTitle.textContent = pTopic.TopicTitle || 'Help'; }

		let tmpDocs = Array.isArray(pTopic.Docs) ? pTopic.Docs : [];
		let tmpLanding = pTopic.Landing || (tmpDocs[0] && tmpDocs[0].Path) || '';
		let tmpTabs = this._el('HelpDrawer-Tabs');
		if (tmpTabs)
		{
			if (tmpDocs.length > 1)
			{
				tmpTabs.innerHTML = tmpDocs.map((pDoc) =>
					'<button class="ps-help-tab" data-path="' + this._attr(pDoc.Path) + '" onclick="_Pict.views[\'Plansheet-HelpDrawer\'].selectDoc(\'' + this._attr(pDoc.Path) + '\')">'
					+ this._escape(pDoc.Title || pDoc.Path) + '</button>').join('');
				tmpTabs.style.display = '';
			}
			else { tmpTabs.innerHTML = ''; tmpTabs.style.display = 'none'; }
		}
		this.loadDoc(tmpLanding);
	}

	_renderNoTopic()
	{
		let tmpTitle = this._el('HelpDrawer-Title');
		if (tmpTitle) { tmpTitle.textContent = 'Help'; }
		let tmpTabs = this._el('HelpDrawer-Tabs');
		if (tmpTabs) { tmpTabs.innerHTML = ''; tmpTabs.style.display = 'none'; }
		this.loadDoc('index.md');
	}

	// Switch documents within the current topic (tab click).
	selectDoc(pPath) { this.loadDoc(pPath); }

	loadDoc(pPath)
	{
		let tmpPath = String(pPath || '').replace(/^\/+/, '');
		this._ActivePath = tmpPath;
		this._markActiveTab(tmpPath);
		let tmpBody = this._el('HelpDrawer-Body');
		if (!tmpBody) { return; }
		if (!tmpPath) { tmpBody.innerHTML = ''; return; }
		libFrameworkClient.getText(this.pict, this._HelpBaseURL + tmpPath)
			.catch(() => '')
			.then((pMarkdown) =>
			{
				// The user may have clicked another tab while this was in flight; only paint the latest.
				if (this._ActivePath !== tmpPath) { return; }
				let tmpTarget = this._el('HelpDrawer-Body');
				if (!tmpTarget) { return; }
				libMarkdownRender.renderInto(this._contentProvider(), tmpTarget, pMarkdown,
					'<p class="ps-help-empty">This help page is not available.</p>');
				tmpTarget.scrollTop = 0;
			});
	}

	_markActiveTab(pPath)
	{
		let tmpTabs = this._el('HelpDrawer-Tabs');
		if (!tmpTabs) { return; }
		let tmpButtons = tmpTabs.querySelectorAll('.ps-help-tab');
		for (let i = 0; i < tmpButtons.length; i++)
		{
			let tmpIsActive = (tmpButtons[i].getAttribute('data-path') === pPath);
			tmpButtons[i].classList.toggle('is-active', tmpIsActive);
		}
	}

	_escape(pText) { return String(pText == null ? '' : pText).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;'); }
	_attr(pText) { return String(pText == null ? '' : pText).replace(/'/g, '&#39;').replace(/"/g, '&quot;'); }
}

module.exports = PictViewHelpDrawer;

module.exports.default_configuration =
{
	ViewIdentifier: 'Plansheet-HelpDrawer',
	DefaultRenderable: 'Plansheet-HelpDrawer',
	DefaultDestinationAddress: '#Plansheet-HelpDrawer',
	AutoRender: false,
	CSS: /*css*/`
		/* Transparent so the drawer body shows the panel's cool brand wash (set on the shell's right
		   panel), which is what differentiates help from the navigation and content. */
		.ps-help { display: flex; flex-direction: column; height: 100%; min-height: 0;
			background: transparent; color: var(--theme-color-text-primary, #1d2230); }
		.ps-help-head { flex: 0 0 auto; display: flex; align-items: center; gap: 8px;
			padding: 12px 14px; border-bottom: 1px solid var(--theme-color-border-default, #e3e6ec); }
		.ps-help-title { flex: 1; font-size: 15px; font-weight: 600; letter-spacing: 0.01em; }
		.ps-help-close { flex: 0 0 auto; width: 28px; height: 28px; border: none; border-radius: 6px;
			background: transparent; color: var(--theme-color-text-secondary, #5b6376); cursor: pointer;
			font-size: 18px; line-height: 1; display: flex; align-items: center; justify-content: center; }
		.ps-help-close:hover { background: var(--theme-color-background-hover, #eef1f6); color: var(--theme-color-text-primary, #2c3140); }
		.ps-help-tabs { flex: 0 0 auto; display: flex; flex-wrap: wrap; gap: 4px; padding: 8px 12px;
			border-bottom: 1px solid var(--theme-color-border-default, #e3e6ec); }
		.ps-help-tab { padding: 4px 10px; font-size: 12px; border: 1px solid var(--theme-color-border-default, #d8dde6);
			border-radius: 14px; background: var(--theme-color-background-panel, #fff);
			color: var(--theme-color-text-secondary, #5b6376); cursor: pointer; }
		.ps-help-tab:hover { border-color: var(--theme-color-brand-primary, #3b6fd4); color: var(--theme-color-text-primary, #2c3140); }
		.ps-help-tab.is-active { background: var(--theme-color-brand-primary, #3b6fd4);
			border-color: var(--theme-color-brand-primary, #3b6fd4); color: var(--theme-color-text-on-brand, #fff); }
		.ps-help-body { flex: 1; min-height: 0; overflow-y: auto; padding: 14px 16px 28px; }
		.ps-help-empty { color: var(--theme-color-text-secondary, #5b6376); font-style: italic; }
		/* The shared content styles (.pict-content) come from pict-section-content; nudge the drawer's
		   reading rhythm a touch tighter than the full page. */
		.ps-help-body .pict-content { font-size: 14px; line-height: 1.55; }
		.ps-help-body .pict-content h1 { font-size: 18px; margin: 0 0 10px; }
		.ps-help-body .pict-content h2 { font-size: 15px; margin: 18px 0 8px; }
		.ps-help-body .pict-content h3 { font-size: 13.5px; margin: 14px 0 6px; }
		.ps-help-body .pict-content ul, .ps-help-body .pict-content ol { padding-left: 20px; }
		.ps-help-body .pict-content li { margin: 3px 0; }
		.ps-help-body .pict-content code { font-size: 12.5px; }
	`,
	Templates: [{
		Hash: 'Plansheet-HelpDrawer',
		Template: /*html*/`
<div class="ps-help">
	<div class="ps-help-head">
		<span class="ps-help-title" id="HelpDrawer-Title">Help</span>
		<button class="ps-help-close" title="Close help (Esc)" onclick="_Pict.PictApplication.closeHelpDrawer()">&times;</button>
	</div>
	<div class="ps-help-tabs" id="HelpDrawer-Tabs" style="display:none;"></div>
	<div class="ps-help-body" id="HelpDrawer-Body"></div>
</div>`
	}],
	Renderables: [{
		RenderableHash: 'Plansheet-HelpDrawer',
		TemplateHash: 'Plansheet-HelpDrawer',
		ContentDestinationAddress: '#Plansheet-HelpDrawer'
	}]
};
